// Catalog.ID Client — Recovery Sheet Verification Page (/recovery)
// ============================================================================
// The page printed on every Recovery Sheet's verification line ("Verify this
// sheet at <host>/recovery", §6.1/§6.2). It is anonymous by necessity: the
// person holding the sheet is a custodian, not the member — a sibling, a
// notary, an archive clerk — and may have no Catalog.ID account of their own
// and no way to reach the member.
//
// What the page does (§6.4):
//   • checks the payload's shape and its MAC under the verification key the
//     payload itself carries — locally, no network. That MAC is the integrity
//     check: round 6's second symbol beside the QR repeated 8 bytes of it and
//     was removed again in round 7 (§14.1);
//   • shows the sheet fingerprint LARGE, so the custodian can compare it with
//     the one printed on the sheet and know that page and paper agree;
//   • names the account and says whether the sheet is bound to it;
//   • asks the node the one remaining question: is this set the current kit?
//
// What the page never does: reconstruct. A page that could reconstruct from a
// single sheet would contradict the threshold. It also never transmits the
// share: only the recovery-set id is posted, and that is stated on the page as
// well as enforced in the code below.
//
// It must work standalone AND inside the desktop dashboard iframe, so every
// internal link goes through dashboardHref().

import { createElement } from '@catalog-shell';
import { createLiveScanner } from '../components/card-scan';
import { decodeRecoveryCard, looksLikeRecoveryCard, type RecoveryCardPayload } from '../crypto/recovery-card';
import { verifyRecoverySet } from '../api/recovery';
import { assessSheet, type SheetVerdict, type VerifyAnswer } from './recovery-verify';
import { dashboardHref } from '../state/dashboard-context';

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
    natural_person: 'Personal account',
    organization: 'Organization',
    delegate: 'Delegate',
    agent: 'Agent',
};

export function renderRecoveryView(container: HTMLElement): void {
    container.innerHTML = '';

    const wrapper = createElement('div', { className: 'cid-auth-container cid-recovery-container' });
    const card = createElement('div', { className: 'cid-auth-card cid-recovery-card' });

    card.appendChild(createElement('h2', {
        className: 'cid-auth-title',
        textContent: 'Verify a Recovery Sheet',
    }));
    card.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'Scan the QR code on a Catalog.ID Recovery Sheet — or upload a photo, a '
            + 'scan, or the kit PDF — to check that it is intact and still part of the account '
            + 'holder\'s current recovery kit. You do not need an account to do this.',
    }));

    card.appendChild(buildVerifySection());
    card.appendChild(buildRecoverAccessSection());
    card.appendChild(buildKeyBackupSection());

    wrapper.appendChild(card);
    container.appendChild(wrapper);
}

// ── (a) Verify a sheet ──────────────────────────────────────────────────────

function buildVerifySection(): HTMLElement {
    const section = createElement('div', { className: 'cid-recovery-section' });
    section.appendChild(sectionTitle('Verify a sheet'));

    const scanArea = createElement('div');
    const resultArea = createElement('div');
    section.appendChild(scanArea);
    section.appendChild(resultArea);

    // Said in the interface as well as enforced in the code: the only thing
    // that leaves this device is the recovery-set number.
    section.appendChild(createElement('p', {
        className: 'cid-pane-note cid-recovery-privacy',
        textContent: 'Only the recovery set number from the QR code is sent to this node. '
            + 'The share itself is read and checked on this device and never leaves it.',
    }));

    const scanner = createLiveScanner({
        uploadLabel: 'Upload a photo, scan or PDF of the sheet…',
        pdf: {
            // A custodian is often handed the kit as a PDF rather than a
            // printout, and the sheets sit well into it — so the whole kit is
            // searched, page by page.
            maxPages: 20,
            sheetLabel: 'Recovery Sheet',
            // Skip past the kit's OTHER QR (the Account Unlock Sheet, which is
            // printed before the sheets): decoding that one here would report a
            // perfectly good kit as a damaged sheet. Magic only, so a genuinely
            // damaged Recovery Sheet still reaches the verdict below.
            accept: looksLikeRecoveryCard,
        },
        onDecoded: (bytes) => { void showVerdict(bytes); },
    });
    // The camera starts from the scanner's own button, never on paint.
    scanArea.appendChild(scanner.element);

    async function showVerdict(rawBytes: Uint8Array): Promise<void> {
        scanArea.style.display = 'none';
        resultArea.innerHTML = '';
        resultArea.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'Checking…',
        }));

        // Decode locally first. A payload that fails its MAC still produces a
        // verdict — "this sheet is damaged" is an answer the custodian needs.
        let payload: RecoveryCardPayload | null = null;
        let decodeError: string | null = null;
        try {
            payload = decodeRecoveryCard(rawBytes);
        } catch (err) {
            decodeError = err instanceof Error ? err.message : 'The QR code is not a Recovery Sheet payload';
        }

        // The node is asked ONLY about the set id, and only when we have one.
        let server: VerifyAnswer | null = null;
        if (payload) {
            try {
                server = await verifyRecoverySet(payload.recoverySetId);
            } catch {
                // Offline, blocked, rate-limited — anything short of an answer
                // leaves currency "unchecked" rather than inventing a verdict.
                server = null;
            }
        }

        const verdict = await assessSheet({ rawBytes, payload, server });
        resultArea.innerHTML = '';
        resultArea.appendChild(renderVerdict(verdict, decodeError, () => {
            resultArea.innerHTML = '';
            scanArea.style.display = '';
            void scanner.resume();
        }));
    }

    return section;
}

function renderVerdict(
    verdict: SheetVerdict,
    decodeError: string | null,
    onScanAnother: () => void,
): HTMLElement {
    const el = createElement('div', { className: 'cid-recovery-verdict' });

    // ── The fingerprint, large. This is the comparison that ties this page to
    // the paper in the custodian's hand, so it outranks everything else.
    if (verdict.fingerprint) {
        el.appendChild(createElement('div', {
            className: 'cid-recovery-fingerprint',
            textContent: verdict.fingerprint,
        }));
        el.appendChild(createElement('p', {
            className: 'cid-recovery-fingerprint-caption',
            textContent: 'Compare with the fingerprint printed on the sheet\'s verification line, '
                + 'under the QR code. If they differ, this page is not describing the sheet you '
                + 'are holding.',
        }));
    }

    const status = createElement('div', {
        className: `cid-recovery-status cid-recovery-status--${verdict.integrity === 'failed'
            ? 'bad'
            : verdict.currency === 'current' && verdict.account?.bound
                ? 'good'
                : 'warn'}`,
    });
    for (const line of verdict.lines) {
        status.appendChild(createElement('p', { className: 'cid-recovery-line', textContent: line }));
    }
    if (decodeError) {
        status.appendChild(createElement('p', {
            className: 'cid-recovery-line cid-recovery-line--detail',
            textContent: decodeError,
        }));
    }
    el.appendChild(status);

    if (verdict.sheet) {
        const facts = createElement('div', { className: 'cid-recovery-facts' });
        const s = verdict.sheet;
        addFact(facts, 'Sheet', `${s.letter} — sheet type ${s.shareIndex} of ${s.totalSharesN}`);
        addFact(facts, 'Quorum', `${s.thresholdK} of ${s.totalSharesN} distinct sheet types required`);
        if (s.purpose) addFact(facts, 'Purpose', s.purpose);
        addFact(facts, 'Kit created', formatDate(s.createdAt));
        if (verdict.account) {
            addFact(facts, 'Account', verdict.account.cid);
            addFact(facts, 'Account type',
                ACCOUNT_TYPE_LABELS[verdict.account.accountType] ?? verdict.account.accountType);
            addFact(facts, 'Sheet is bound to this account', verdict.account.bound ? 'Yes' : 'No');
        }
        el.appendChild(facts);

        // Standing reminder: one sheet is not a key, whatever the verdict said.
        el.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: `A single sheet reconstructs nothing — ${s.thresholdK} different sheet `
                + 'types are needed, and this page never attempts a reconstruction.',
        }));
    }

    const actions = createElement('div', { className: 'cid-credential-actions' });
    const again = createElement('button', {
        className: 'cs-btn cs-btn--default cid-credential-action',
        textContent: 'Scan another',
    });
    again.addEventListener('click', onScanAnother);
    actions.appendChild(again);
    el.appendChild(actions);

    return el;
}

// ── (b) Recover access ──────────────────────────────────────────────────────

function buildRecoverAccessSection(): HTMLElement {
    const section = createElement('div', { className: 'cid-recovery-section' });
    section.appendChild(sectionTitle('Recover access'));

    section.appendChild(createElement('p', {
        className: 'cid-recovery-para',
        textContent: 'Verifying a sheet does not unlock anything. To actually recover an '
            + 'account you need a quorum — the number of different sheet types printed on '
            + 'the sheet itself, typically two of three. Copies of the same sheet type do '
            + 'not count.',
    }));
    section.appendChild(createElement('p', {
        className: 'cid-recovery-para',
        textContent: 'With a quorum in hand, open the login screen, choose "Use supersecret '
            + 'instead", then "Recover with Recovery Sheets", and scan the sheets one by one. '
            + 'The supersecret is reassembled in the browser and never sent anywhere.',
    }));
    section.appendChild(linkRow('Open the login screen', '/'));

    return section;
}

// ── (c) Restore from an offline key backup ──────────────────────────────────

function buildKeyBackupSection(): HTMLElement {
    const section = createElement('div', { className: 'cid-recovery-section' });
    section.appendChild(sectionTitle('Restore from an offline key backup'));

    section.appendChild(createElement('p', {
        className: 'cid-recovery-para',
        textContent: 'If the account\'s keys are held in an encrypted key backup file rather '
            + 'than on this node, the same unlock screen accepts that file together with the '
            + 'supersecret reassembled from a quorum of sheets. The file is useless without '
            + 'the supersecret, and the supersecret is useless without a quorum.',
    }));
    section.appendChild(linkRow('Open the login screen', '/'));

    return section;
}

// ── small builders ──────────────────────────────────────────────────────────

function sectionTitle(text: string): HTMLElement {
    return createElement('h3', { className: 'cid-recovery-section-title', textContent: text });
}

function linkRow(text: string, path: string): HTMLElement {
    const row = createElement('div', { className: 'cid-auth-link-row' });
    const link = createElement('a', { className: 'cid-auth-link', textContent: text });
    // dashboardHref keeps ?dp=<port> on internal links so a click inside the
    // desktop dashboard iframe does not boot the client in standalone mode.
    link.setAttribute('href', dashboardHref(path));
    row.appendChild(link);
    return row;
}

function addFact(parent: HTMLElement, label: string, value: string): void {
    const row = createElement('div', { className: 'cid-key-field' });
    row.appendChild(createElement('span', { className: 'cid-key-field-label', textContent: label }));
    row.appendChild(createElement('span', { className: 'cid-key-field-value', textContent: value }));
    parent.appendChild(row);
}

function formatDate(date: Date): string {
    try {
        return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
        return date.toISOString().slice(0, 10);
    }
}
